import { Container, Stack, Box, IconButton } from "@mui/material";
import { useState, useEffect, useMemo } from "react";
import TabContext from "@mui/lab/TabContext";
import FinishedOrders from "./FinishedOrders";
import PausedOrders from "./PausedOrders";
import ProcessOrders from "./ProcessOrders";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import FilterListIcon from "@mui/icons-material/FilterList";
import PauseCircleOutlineIcon from "@mui/icons-material/PauseCircleOutline";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useDispatch, useSelector } from "react-redux";
import { Dispatch } from "@reduxjs/toolkit";
import { setPausedOrders, setProcessOrders, setFinishedOrders } from "./slice";
import { retrievePausedOrders, retrieveProcessOrders, retrieveFinishedOrders } from "./selector";
import { Order, OrderInquiry } from "../../../lib/types/order";
import { OrderStatus } from "../../../lib/enums/order.enum";
import OrderService from "../../services/OrderService";
import { useGlobals } from "../../hooks/useGlobals";
import "../../../css/order.css";
import { useHistory } from "react-router-dom";
import { serverApi } from "../../../lib/config";
import { MemberType } from "../../../lib/enums/member.enum";
/** REDUX SLICE & SELECTOR */
const actionDispatch = (dispatch: Dispatch) => ({ // reducer ga beryapmiz
    setPausedOrders: (data: Order[]) => dispatch(setPausedOrders(data)),
    setProcessOrders: (data: Order[]) => dispatch(setProcessOrders(data)),
    setFinishedOrders: (data: Order[]) => dispatch(setFinishedOrders(data)),
});


const filterOptions = [
  { value: "1", label: "Paused Orders", Icon: PauseCircleOutlineIcon },
  { value: "2", label: "Processed Orders", Icon: LocalShippingIcon },
  { value: "3", label: "Received Orders", Icon: CheckCircleIcon },
];

export default function OrdersPage() {
  const dispatch = useDispatch();
  const { setPausedOrders, setProcessOrders, setFinishedOrders } = useMemo(
    () => actionDispatch(dispatch),
    [dispatch]
  );
  const pausedOrders = useSelector(retrievePausedOrders);
  const processOrders = useSelector(retrieveProcessOrders);
  const finishedOrders = useSelector(retrieveFinishedOrders);
  const { authMember, orderBuilder } = useGlobals();
  const history = useHistory();
  const [value, setValue] = useState("1");
  const [filterOpen, setFilterOpen] = useState(false);

  const [orderInquiry] = useState<OrderInquiry>({
    page: 1,
    limit: 5,
    orderStatus: OrderStatus.PAUSE,
  });

  useEffect(() => {
    const order = new OrderService();
    order
      .getMyOrders({ ...orderInquiry, orderStatus: OrderStatus.PAUSE })
      .then((data) => setPausedOrders(data))
      .catch((err) => console.log(err));
    order
      .getMyOrders({ ...orderInquiry, orderStatus: OrderStatus.PROCESS })
      .then((data) => setProcessOrders(data))
      .catch((err) => console.log(err));
    order
      .getMyOrders({ ...orderInquiry, orderStatus: OrderStatus.FINISH })
      .then((data) => setFinishedOrders(data))
      .catch((err) => console.log(err));
  }, [orderInquiry, orderBuilder, setFinishedOrders, setPausedOrders, setProcessOrders]);

  const getFilterCount = (v: string) => {
    if (v === "1") return pausedOrders?.length ?? 0;
    if (v === "2") return processOrders?.length ?? 0;
    if (v === "3") return finishedOrders?.length ?? 0;
    return 0;
  };

  if (!authMember) history.push("/");

  return (
    <div className="order-page">
      {/* Blur overlay when filter panel is open (mobile) */}
      {filterOpen && (
        <div
          className="order-filter-backdrop"
          onClick={() => setFilterOpen(false)}
          aria-hidden
        />
      )}
      <Container className="order-container">
        {/* Left: Profile + Filters column */}
        <Stack className="order-sidebar">
          <Box className="order-info-box order-side-card">
            <h3 className="order-card-title">Profile</h3>
            <Box className="member-box">
              <div className="order-user-img">
                <img
                  src={
                    authMember?.memberImage
                      ? `${serverApi}/${authMember.memberImage}`
                      : "/icons/default-user.svg"
                  }
                  className="order-user-avatar"
                  alt="Profile"
                />
                <div className="order-user-icon-box">
                  <img
                    src={
                      authMember?.memberType === MemberType.RESTAURANT
                        ? "/icons/restaurant.svg"
                        : "/icons/user-badge.svg"
                    }
                    alt=""
                    className="order-user-prof-img"
                  />
                </div>
              </div>
              <span className="order-user-name">{authMember?.memberNick}</span>
              <span className="order-user-prof">{authMember?.memberType}</span>
            </Box>
            <Box className="linear" />
            <Box className="order-user-address" sx={{ display: "flex", alignItems: "center" }}>
              <span className="order-address-icon">
                <LocationOnIcon />
              </span>
              <p className="spec-adress-txt">
                {authMember?.memberAdress ? authMember.memberAdress : "Do not exist"}
              </p>
            </Box>
          </Box>

          <Box
            className={`order-filters-panel ${
              filterOpen ? "order-filters-panel--open" : ""
            }`}
          >
            <Box className="order-filters-card">
              <h3 className="order-filters-title">Filters</h3>
              <p className="order-filters-subtitle">Manage your order views.</p>
              <ul className="order-filters-list">
                {filterOptions.map(({ value: v, label, Icon }) => (
                  <li key={v}>
                    <button
                      type="button"
                      className={`order-filter-item ${
                        value === v ? "order-filter-item--active" : ""
                      }`}
                      onClick={() => {
                        setValue(v);
                        setFilterOpen(false);
                      }}
                    >
                      <Icon className="order-filter-icon" />
                      <span className="order-filter-label">{label}</span>
                      {getFilterCount(v) > 0 && (
                        <span className="order-filter-badge">{getFilterCount(v)}</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </Box>
          </Box>
        </Stack>

        {/* Center / right: main content */}
        <Stack className="order-left">
          <Box className="order-page-header order-page-header--with-toggle">
            <IconButton
              className="order-filter-toggle"
              onClick={() => setFilterOpen(true)}
              aria-label="Open filters"
            >
              <FilterListIcon />
            </IconButton>
            <Box>
              <h1 className="order-page-title">My Orders</h1>
              <p className="order-page-subtitle">Track and manage your recent book purchases.</p>
            </Box>
          </Box>
          <TabContext value={value}>
            <Stack className="order-main-context">
              <PausedOrders setValue={setValue} />
              <ProcessOrders setValue={setValue} />
              <FinishedOrders />
            </Stack>
          </TabContext>
        </Stack>
        {/* Payment details section temporarily disabled */}
        {/*
        {showSidebar && (
          <Stack className="order-right">
            <Box className="order-info-box order-side-card">
              <h3 className="order-card-title">Payment details</h3>
              <p className="order-card-subtitle">This is a demo form (not charged).</p>

              <Box className="member-box payment-form">
                <Box className="payment-field">
                  <input
                    type="text"
                    inputMode="numeric"
                    placeholder="Card number (e.g. 5243 4090 2002 7495)"
                    className="input1"
                  />
                </Box>

                <Stack className="cvv payment-row">
                  <Box className="payment-field">
                    <input type="text" inputMode="numeric" placeholder="MM / YY" className="cv1" />
                  </Box>
                  <Box className="payment-field">
                    <input type="text" inputMode="numeric" placeholder="CVV" className="cv1" />
                  </Box>
                </Stack>

                <Box className="payment-field">
                  <input type="text" placeholder="Cardholder name" className="input2" />
                </Box>

                <Stack className="cards-box payment-methods">
                  <Box><img src="/icons/western-card.svg" alt="Western Union" /></Box>
                  <Box><img src="/icons/master-card.svg" alt="Mastercard" /></Box>
                  <Box><img src="/icons/paypal-card.svg" alt="PayPal" /></Box>
                  <Box><img src="/icons/visa-card.svg" alt="Visa" /></Box>
                </Stack>
              </Box>
            </Box>
          </Stack>
        )}
        */}
        </Container>
    </div>
  );
}